import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Alert, Button, Card, Select, Space, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { AsyncBoundary, EM_DASH, errorMessage, formatDateTime, space } from '@viq-admin/ui';
import { listLeads, type LeadsFilter } from '../../api/leads';
import type { CaptureType, Lead } from '@viq-admin/types';
import { RequireCan } from '../../components/RequireCan';
import { useCompanyScope } from '../../components/CompanyScopePicker';
import { CompanyLink, ConferenceLink, LeadLink, UserLink } from '../../components/EntityLinks';
import { leadHref } from '../../components/entityHrefs';
import { useAllConferences, useAllUsers, useConferenceNameMap } from '../../lookups/useLookups';
import { LEADS_URL_PARAMS, readLeadsUrlFilter } from './leadsHref';

/**
 * Cross-tenant, read-only lead list for the super-admin plane.
 *
 * The mobile/customer app owns capture + all mutation; super-admin only reads.
 * This is intentionally a stripped-down view — filters + cursor pagination +
 * linked company / rep / conference columns — with NONE of the customer
 * BatchScreen's bulk-tag/assign/export machinery and no write affordances.
 * `read Lead` gates the whole view.
 *
 * Not a Refine resource: it calls the typed `listLeads` wrapper directly
 * (useState/useEffect) rather than going through a dataProvider/useTable.
 *
 * THE URL IS THE FILTER. `companyId`, `repOid` and `conferenceId` follow the
 * Leads URL contract in `leadsHref.ts`, so company / user / conference pages
 * link straight into a pre-filtered list ("See all leads →"), and a reload or
 * a pasted link keeps the filter. `captureType` rides along in the URL the
 * same way. Filter changes REPLACE the history entry, so Back leaves the list
 * rather than stepping through every filter tweak.
 *
 * Explicit filters only: the list ignores the global "Operate as" scope (it
 * never did), so a company filter is always a visible, clearable choice here.
 *
 * NOTE ON THE COMPANY COLUMN: the real `Lead` type carries two company-ish
 * fields — `companyName` (the *contact's* employer string, decoupled from the
 * tenant) and `companyId` (the VendoIQ-internal tenant this lead belongs to).
 * Because this is the cross-tenant super-admin view, the meaningful "which
 * tenant" column is `companyId`, shown as the tenant's name; that is also
 * what the company filter narrows on.
 */

const CAPTURE_COLOR: Record<CaptureType, string> = {
  voice: 'purple',
  badge: 'blue',
  card: 'green',
};

const CAPTURE_OPTIONS: { label: string; value: CaptureType }[] = [
  { label: 'Voice', value: 'voice' },
  { label: 'Badge', value: 'badge' },
  { label: 'Card', value: 'card' },
];

const isCaptureType = (v: string | null): v is CaptureType =>
  CAPTURE_OPTIONS.some((o) => o.value === v);

/** The URL keys this list owns; anything else in the query string is left alone. */
type FilterKey = (typeof LEADS_URL_PARAMS)[number] | 'captureType';

/** Case-insensitive match on an option's string label, for the searchable selects. */
const matchLabel = (input: string, option?: { label?: unknown }) =>
  String(option?.label ?? '').toLowerCase().includes(input.toLowerCase());

export function LeadsList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { companies } = useCompanyScope();
  const { conferences, isLoading: conferencesLoading } = useAllConferences();
  const conferenceNames = useConferenceNameMap();

  // Read each filter as a primitive so `filters` (and the load below) only
  // changes identity when a filter value actually changes, not on every
  // unrelated search-param update.
  const urlFilter = readLeadsUrlFilter(searchParams);
  const { companyId, repOid, conferenceId } = urlFilter;
  const rawCaptureType = searchParams.get('captureType');
  const captureType = isCaptureType(rawCaptureType) ? rawCaptureType : undefined;
  const filters = useMemo<LeadsFilter>(() => {
    const f: LeadsFilter = {};
    if (companyId) f.companyId = companyId;
    if (repOid) f.repOid = repOid;
    if (conferenceId) f.conferenceId = conferenceId;
    if (captureType) f.captureType = captureType;
    return f;
  }, [companyId, repOid, conferenceId, captureType]);

  // The rep filter has no picker (it arrives from a user page's link), only a
  // closable tag; the cross-tenant user list resolves its name. Fetched only
  // while a rep filter is set.
  const { users, isLoading: usersLoading } = useAllUsers({ enabled: !!repOid });

  const setFilter = useCallback(
    (key: FilterKey, value: string | undefined) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [leads, setLeads] = useState<Lead[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Two error slots, keyed by which request failed: an initial/filter-driven
  // load (no cursor) vs. a "Load more" page fetch (cursor set). Keeping them
  // separate means a failed "Load more" can show its own error without
  // routing through the AsyncBoundary below and swapping out the rows the
  // operator can already see.
  const [error, setError] = useState<unknown>(null);
  const [loadMoreError, setLoadMoreError] = useState<unknown>(null);
  // Only the newest request may write state. Filters now change on every
  // select pick, so an earlier, slower response must not land on top of the
  // current filter's rows.
  const requestSeq = useRef(0);

  const load = useCallback(
    async (cursor?: string) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      if (cursor) {
        setLoadMoreError(null);
      } else {
        setError(null);
        setLoadMoreError(null);
      }
      try {
        const page = await listLeads(cursor ? { ...filters, cursor } : filters);
        if (seq !== requestSeq.current) return;
        setLeads((prev) => (cursor ? [...prev, ...page.leads] : page.leads));
        setNextCursor(page.nextCursor);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        // Surface the failure in the UI instead of only logging it — a bare
        // console.error here left the operator staring at an empty or stuck
        // table with no explanation of what went wrong or how to recover.
        if (cursor) {
          setLoadMoreError(err);
        } else {
          setError(err);
        }
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const companyOptions = useMemo(() => {
    const opts = [...companies]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({ label: c.name, value: c.id }));
    // A filter from a link to an archived tenant (the scope list holds active
    // tenants only) still needs a label, never the bare id.
    if (companyId && !opts.some((o) => o.value === companyId)) {
      opts.unshift({ label: companies.length ? 'Archived company' : 'Loading…', value: companyId });
    }
    return opts;
  }, [companies, companyId]);

  const conferenceOptions = useMemo(() => {
    const opts = conferences.map((c) => ({ label: c.name, value: c.id }));
    // Same for an archived conference: the lookup lists active ones only.
    if (conferenceId && !opts.some((o) => o.value === conferenceId)) {
      opts.unshift({
        label: conferencesLoading ? 'Loading…' : 'Archived conference',
        value: conferenceId,
      });
    }
    return opts;
  }, [conferences, conferencesLoading, conferenceId]);

  const repName = repOid
    ? users.find((u) => u.oid === repOid)?.name ||
      leads.find((l) => l.repOid === repOid)?.repName ||
      (usersLoading ? 'Loading…' : 'Unknown rep')
    : undefined;

  const columns: TableProps<Lead>['columns'] = [
    {
      key: 'lead',
      title: 'Lead',
      render: (_: unknown, r: Lead) => <LeadLink id={r.id} name={r.preview} />,
    },
    {
      // Tenant (see the note above), named from the tenant list.
      key: 'company',
      title: 'Company',
      render: (_: unknown, r: Lead) => <CompanyLink id={r.companyId} />,
    },
    {
      key: 'rep',
      title: 'Rep',
      render: (_: unknown, r: Lead) => <UserLink oid={r.repOid} name={r.repName} />,
    },
    {
      key: 'conference',
      title: 'Conference',
      render: (_: unknown, r: Lead) => (
        <ConferenceLink id={r.conferenceId} name={conferenceNames.get(r.conferenceId)} />
      ),
    },
    {
      dataIndex: 'captureType',
      title: 'Type',
      width: 100,
      render: (v: CaptureType | null) =>
        v ? <Tag color={CAPTURE_COLOR[v] ?? 'default'}>{v}</Tag> : EM_DASH,
    },
    {
      dataIndex: 'createdAt',
      title: 'Captured',
      width: 190,
      render: (v: string) => formatDateTime(v),
      sorter: (a: Lead, b: Lead) => a.createdAt.localeCompare(b.createdAt),
      defaultSortOrder: 'descend',
    },
  ];

  const loadedLabel = useMemo(
    () =>
      `${leads.length} loaded${nextCursor ? ' — more available' : ' (end)'}`,
    [leads.length, nextCursor],
  );

  return (
    <RequireCan I="read" a="Lead">
      <div style={{ padding: space.lg }}>
        <Card>
          <Space wrap>
            <Select
              aria-label="Company"
              placeholder="All companies"
              style={{ width: 260, maxWidth: '100%' }}
              options={companyOptions}
              value={companyId}
              onChange={(v: string | undefined) => setFilter('companyId', v)}
              allowClear
              showSearch
              filterOption={matchLabel}
            />
            <Select
              aria-label="Conference"
              placeholder="All conferences"
              style={{ width: 260, maxWidth: '100%' }}
              options={conferenceOptions}
              value={conferenceId}
              onChange={(v: string | undefined) => setFilter('conferenceId', v)}
              allowClear
              showSearch
              filterOption={matchLabel}
            />
            <Select
              aria-label="Type"
              placeholder="All types"
              style={{ width: 140, maxWidth: '100%' }}
              options={CAPTURE_OPTIONS}
              value={captureType}
              onChange={(v: CaptureType | undefined) => setFilter('captureType', v)}
              allowClear
            />
            {repOid ? (
              <Tag
                closable={{ 'aria-label': 'Clear rep filter' }}
                onClose={() => setFilter('repOid', undefined)}
              >
                Rep: <UserLink oid={repOid} name={repName} />
              </Tag>
            ) : null}
          </Space>
        </Card>
        <Card style={{ marginTop: space.lg }}>
          {/*
            AsyncBoundary is consulted for `error` only (no `loading` prop) —
            the initial/filter-driven load has nothing on screen yet, so its
            failure can safely replace this card's body with the shared error
            + Retry treatment. Loading itself stays on the Table's own
            `loading` overlay (below) so it never wipes existing rows; a
            "Load more" failure is handled separately, via `loadMoreError`,
            so it never routes through this swap and never hides rows that
            already loaded successfully.
          */}
          <AsyncBoundary error={error} onRetry={() => void load()}>
            <Table
              rowKey="id"
              dataSource={leads}
              columns={columns}
              loading={loading}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              // A row click opens the lead. The name, company, rep and
              // conference cells are links that stop propagation, so
              // following one never also fires this. Keyboard users reach
              // the lead through the Lead column's link.
              onRow={(r) => ({
                onClick: () => navigate(leadHref(r.id)),
                style: { cursor: 'pointer' },
              })}
            />
            <div style={{ marginTop: space.md }}>
              <Space direction="vertical" align="end" style={{ width: '100%' }}>
                {loadMoreError ? (
                  <Alert
                    type="error"
                    showIcon
                    message={errorMessage(
                      loadMoreError,
                      'Failed to load more leads.',
                    )}
                    action={
                      <Button
                        size="small"
                        onClick={() => void load(nextCursor ?? undefined)}
                      >
                        Retry
                      </Button>
                    }
                    style={{ width: '100%' }}
                  />
                ) : null}
                <Space wrap>
                  <Typography.Text type="secondary">{loadedLabel}</Typography.Text>
                  <Button
                    onClick={() => void load(nextCursor ?? undefined)}
                    disabled={!nextCursor || loading}
                  >
                    Load more
                  </Button>
                </Space>
              </Space>
            </div>
          </AsyncBoundary>
        </Card>
      </div>
    </RequireCan>
  );
}
